import { CoachSettings, HashTagElementResponse } from "@/interfaces";
import * as React from "react";
import { useEffect, useMemo } from "react";
import "./styles.scss";
import { HashTagsServices } from "@/services";
import { descriptors, VideoProjectComponentType } from "../../descriptors";
import TextareaAutosize from "react-textarea-autosize";
import { Form, Grid } from "semantic-ui-react";
import { useIntl } from "react-intl";
import { HashTagSet } from "../HashTagSet";
import { usePlayerSettings, useCoachSettings, useComplianceChecker } from "@/utils";
import { CaptionCoachDialog } from "@/components/CaptionCoach/CaptionCoachDialog";

interface ProjectCaptionParams {
  coachId?: string | number;
  playerId?: string | number;
  role: "PLAYER" | "COACH";
  includePlayerHashtags: boolean;
  captionValue: string;
  disabled: boolean;
  onUpdateCaption: (caption: string) => void;
  onUpdateHashtags: (hashtags: string) => void;
  onUpdateWarnings: (warnings: string[]) => void;
  onUpdateErrors: (errors: string[]) => void;
}

type Props = ProjectCaptionParams;

export const ProjectCaption: React.FC<Props> = ({
  coachId,
  playerId,
  role,
  includePlayerHashtags,
  captionValue,
  disabled,
  onUpdateCaption,
  onUpdateHashtags,
  onUpdateWarnings,
  onUpdateErrors,
}) => {
  const { formatMessage } = useIntl();
  const [showAIModal, setShowAIModal] = React.useState<boolean>(false);
  const [caption, setCaption] = React.useState<string>(captionValue);
  const [defaultHashtags, setDefaultHashtags] = React.useState<HashTagElementResponse[]>([]);
  const [hashtags, setHashtags] = React.useState<string[]>(["videocatalyst"]);
  const MAX_HASHTAGS = 30;
  const { findBlockedWords, findFlaggedWords } = useComplianceChecker({ playerId, coachId });

  const { hasSetting: hasPlayerSetting } = usePlayerSettings(playerId);
  const { hasSetting: hasCoachSetting } = useCoachSettings(coachId);

  const hasCaptionCoach = useMemo(() => {
    return playerId
      ? hasPlayerSetting(CoachSettings.CAPTION_COACH, true)
      : hasCoachSetting(CoachSettings.CAPTION_COACH, true);
  }, [hasPlayerSetting, hasCoachSetting, playerId]);

  useEffect(() => {
    const getDefaultHashtags = async (): Promise<HashTagElementResponse[]> => {
      switch (role) {
        case "COACH":
          if (playerId) {
            return await HashTagsServices.get(playerId, coachId);
          } else if (coachId) {
            return await HashTagsServices.getCoachHashtags(coachId);
          }
          break;
        case "PLAYER":
          if (playerId) {
            return await HashTagsServices.get(playerId!!, coachId);
          }
          break;
      }
      return [];
    };

    getDefaultHashtags().then(setDefaultHashtags);
  }, [coachId, playerId, role, includePlayerHashtags]);

  useEffect(() => {
    setHashtags(() => {
      const tagsToExclude = (includePlayerHashtags ? [] : defaultHashtags.filter(t => !!t.playerId)).map(t =>
        t.content.toLowerCase()
      );
      const defaultTags = defaultHashtags.map(t => t.content);

      const newTags: string[] = [];
      [...hashtags, ...defaultTags].forEach(tag => {
        if (!tagsToExclude.includes(tag.toLowerCase()) && !newTags.some(t => t.toLowerCase() === tag.toLowerCase())) {
          newTags.push(tag);
        }
      });
      return newTags;
    });
  }, [defaultHashtags, includePlayerHashtags]);

  const maxHashtagsError: string = useMemo(() => {
    return hashtags.length > MAX_HASHTAGS ? "You can only add a maximum of 30 hashtags" : "";
  }, [hashtags.length]);

  const blockedCaptionError: string = useMemo(() => {
    const blockedCaptionWords = findBlockedWords(caption);
    return blockedCaptionWords.length > 0
      ? `Please remove blocked words from your caption: ${blockedCaptionWords.join(", ")}.`
      : "";
  }, [caption, findBlockedWords]);

  const flaggedCaptionWarning: string = useMemo(() => {
    const flaggedCaptionWords = findFlaggedWords(caption);
    const flaggedHashtagWords = findFlaggedWords(hashtags.join(" "));
    const combinedWords = [...flaggedCaptionWords, ...flaggedHashtagWords];
    return combinedWords.length > 0
      ? `Please review carefully before submitting. The following words are flagged: ${combinedWords.join(", ")}.`
      : "";
  }, [caption, hashtags, findFlaggedWords]);

  const blockedHashtagError: string = useMemo(() => {
    const blockedHashtagWords = findBlockedWords(hashtags.join(" "));
    return blockedHashtagWords.length > 0
      ? `Please remove blocked words from your hashtags: ${blockedHashtagWords.join(", ")}.`
      : "";
  }, [hashtags, findBlockedWords]);

  const validateContent = () => {
    const errors = [blockedCaptionError, blockedHashtagError, maxHashtagsError].filter(
      m => m && m.length > 0
    ) as string[];
    onUpdateErrors(errors);

    let warnings = [flaggedCaptionWarning].filter(m => m && m.length > 0) as string[];
    onUpdateWarnings(warnings);
  };

  useEffect(() => {
    onUpdateCaption(caption);
    validateContent();
  }, [caption]);

  useEffect(() => {
    onUpdateHashtags(hashtags.map(t => `#${t}`).join(" "));
    validateContent();
  }, [hashtags]);

  const hashtagSection = () => {
    return (
      <>
        <label>{formatMessage({ ...descriptors[VideoProjectComponentType.hashtags] })}</label>
        <Grid className={"marginBottom paddingTop"}>
          <HashTagSet
            title={"Hashtags..."}
            customTagStyle={`background: rgba(61, 174, 245, 0.2);`}
            defaultHashtagList={hashtags}
            onSuccess={setHashtags}
          />
        </Grid>
      </>
    );
  };

  const captionSection = () => {
    return (
      <Form.Field className={"captionSection"}>
        <div className={"captionHeader"}>
          <label>{formatMessage({ ...descriptors[VideoProjectComponentType.message] })}</label>
          {hasCaptionCoach && (
            <a className="projectAiButton" onClick={() => setShowAIModal(true)}>
              Generate Caption
            </a>
          )}
        </div>
        <TextareaAutosize
          data-elm-id={"projectCaptionInput"}
          id="messageToPost"
          name="messageToPost"
          className="captionField"
          placeholder={formatMessage({ ...descriptors[VideoProjectComponentType.messagePlaceholder] })}
          rows={3}
          disabled={disabled}
          value={caption}
          onChange={data => {
            setCaption(data.currentTarget.value);
          }}
        />
      </Form.Field>
    );
  };

  return (
    <div className={"captionContainer"}>
      {captionSection()}
      {hashtagSection()}

      {showAIModal && (
        <CaptionCoachDialog
          open={showAIModal}
          onOpenChange={setShowAIModal}
          handleAcceptedContent={({ caption: newCaption, hashtags: addedHashtags }) => {
            setShowAIModal(false);
            setCaption(newCaption);

            if (addedHashtags && addedHashtags.length > 0) {
              const newTags: string[] = [];
              if (hashtags.length > 0) {
                hashtags.forEach(tag => newTags.push(tag));
              }

              addedHashtags.forEach(tag => {
                if (newTags.indexOf(tag) === -1) {
                  newTags.push(tag);
                }
              });
            }
          }}
        />
      )}
    </div>
  );
};
